import { useReadContract } from "wagmi";
import { GATEWAY_ABI } from "@/contract/abi/gateway";
import { GATEWAY_LEGACY_ABI } from "@/contract/abi/gateway-legacy";
import ContractAddress from "@/contract/addresses";
import { IS_GATEWAY_V2 } from "@/contract/gateway-version";

/**
 * DNM already withdrawn from the OLD gateway.
 *
 * Gateway V2 caps a withdrawal at the lifetime entitlement minus what BOTH
 * gateways have paid out (DnmFaucetLib.withdrawPersonal / withdrawSeller). It
 * has to: migration imports users with `dnm_withdraw` zeroed, precisely so a
 * migrated user cannot be paid twice for the same BV.
 *
 * Any UI that subtracts only V2's counter therefore overstates the claimable
 * amount by everything the user ever withdrew on V1, and the transaction
 * reverts with `DnmEntitlementExceeded` when they act on it.
 */

/**
 * The old gateway's address, read off V2 itself. It is an immutable set in the
 * V2 constructor, so this cannot drift the way a separate env var would.
 */
export const useOldGatewayAddress = () => {
  const { data } = useReadContract({
    address: ContractAddress.GATEWAY,
    abi: GATEWAY_ABI,
    functionName: "oldGateway",
    // `oldGateway` does not exist on V1 — calling it there reverts. On a V1
    // build there is no predecessor to consult anyway.
    query: { enabled: IS_GATEWAY_V2 },
  });

  return data;
};

/** Personal (user) DNM withdrawn on V1. Zero while the address is unknown. */
export const useLegacyUserDnmWithdrawn = (
  userAddress: `0x${string}` | undefined,
) => {
  const oldGateway = useOldGatewayAddress();

  const { data, isLoading } = useReadContract({
    address: oldGateway,
    abi: GATEWAY_LEGACY_ABI,
    functionName: "getUser",
    args: userAddress ? [userAddress] : undefined,
    query: { enabled: !!oldGateway && !!userAddress },
  });

  // On a V1 build this gateway IS the only gateway — there is no predecessor
  // to add, and no pending read to wait for. Returning isLoading here would
  // wedge callers that withhold their figure until it settles.
  if (!IS_GATEWAY_V2) {
    return { withdrawn: 0n, isLoading: false };
  }

  return {
    // A never-registered address decodes to a zero-filled struct, which is the
    // right answer here: nothing withdrawn.
    withdrawn: data?.bv.dnm_withdraw ?? 0n,
    // Distinguishable from "genuinely zero" so callers can avoid briefly
    // showing an inflated claimable figure while this is in flight.
    isLoading: !!userAddress && (isLoading || !oldGateway),
  };
};

/** Seller DNM withdrawn on V1. Zero while the address is unknown. */
export const useLegacySellerDnmWithdrawn = (
  sellerAddress: `0x${string}` | undefined,
) => {
  const oldGateway = useOldGatewayAddress();

  const { data, isLoading } = useReadContract({
    address: oldGateway,
    abi: GATEWAY_LEGACY_ABI,
    functionName: "SellerList",
    args: sellerAddress ? [sellerAddress] : undefined,
    query: { enabled: !!oldGateway && !!sellerAddress },
  });

  if (!IS_GATEWAY_V2) {
    return { withdrawn: 0n, isLoading: false };
  }

  return {
    // SellerList returns a flat tuple; index 4 is dnm_withdraw.
    withdrawn: data?.[4] ?? 0n,
    isLoading: !!sellerAddress && (isLoading || !oldGateway),
  };
};
